/**
 * 第一次在私聊里用到 TA 的回信时：一句用途说明 + 隐私说明的入口（REL-01 第 2 项 / REL-02 3.2；c84a D0020-1 / D0150-2）。
 * - 只在回信服务真的在用（model_replies_available）时出现；服务没开、读不到设置时都不出现（不说没发生的事）。
 *   玩家这里不再有开关（运营后台按能力和额度管），不读 model_replies，入口也不说“关闭”。
 * - 那一句用 c84a 原句（DATA-USE-COPY-01，c84a REPLY-I-20261009-31 第 29 项）：内容交给外部语言服务处理照实说，后面跟“查看内容使用说明”，
 *   去 /settings/data-use（原来是“在设置里看隐私说明”，去 /settings#privacy）。不写 AI 字样、不写服务名。
 * - 说明不写成“只影响聊天”：这一项还管主动来信、家庭来信的措辞等，细节在内容使用说明页。
 * - 点“知道了”或点进说明页，这个账号在本机就不再出现（localStorage，读写都兜住；存不了时本次会话内不再出现）。
 * - 设置读的是和设置页同一条查询（键相同），不多一个请求口径。
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { env } from "@/shared/config/env";
import { CHAT_DATA_USE, DataUseLink } from "@/features/identity/dataUse/DataUseNote";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";

const NOTE_KEY = (userId: string | null) => `petsoul:model-reply-note:${userId ?? "fixture"}`;

function readSeen(userId: string | null): boolean {
  try {
    return window.localStorage.getItem(NOTE_KEY(userId)) === "seen";
  } catch {
    return false;
  }
}

function writeSeen(userId: string | null): void {
  try {
    window.localStorage.setItem(NOTE_KEY(userId), "seen");
  } catch {
    /* 存不了：本次会话内不再出现就行 */
  }
}

/** 设置页的查询键（identity/pages.tsx 的 SettingsPage 同一套：演示一份；live 带账号与宠物） */
export function settingsQueryKey(userId: string | null, petId: string | null): readonly unknown[] {
  return env.dataMode === "fixture" ? queryKeys.settings : [...queryKeys.settings, userId ?? "-", petId ?? "-"];
}

export function ModelReplyNote({ userId, petId }: { userId: string | null; petId: string | null }) {
  // 取数时才去拿 session 服务：通讯器别的用例给的替身服务里没有它，读不到就当“读不到设置”（不出现），不让整页出错
  const services = useServices();
  const settings = useQuery({
    queryKey: settingsQueryKey(userId, petId),
    queryFn: () => services.session.settings(petId),
    enabled: env.dataMode === "fixture" || Boolean(userId && petId),
    retry: false,
    staleTime: 60_000,
  });
  const [seen, setSeen] = useState(() => readSeen(userId));
  useEffect(() => setSeen(readSeen(userId)), [userId]);
  const view = settings.data;
  if (seen || !view || view.model_replies_available !== true) return null;
  const dismiss = () => {
    writeSeen(userId);
    setSeen(true);
  };
  return (
    <aside className="ps-comm-model-note" aria-label="关于 TA 的回信">
      {/* 用户 2026-09-29 定：不写“对话模型”这类说法；内容交给外部服务处理照实说（c84a 原句，DATA-USE-COPY-01） */}
      <p>{CHAT_DATA_USE}</p>
      <div className="ps-comm-model-note__actions">
        <DataUseLink onClick={dismiss} />
        <button type="button" onClick={dismiss}>
          知道了
        </button>
      </div>
    </aside>
  );
}
